import { useState } from "react";
import { RecordMetadata } from "@/components/RecordMetadata";
import { ContactConversation } from "@/components/quotes/ContactConversation";
import type { QuoteRow } from "@/hooks/useQuotes";
import type { ContactMessage } from "@/hooks/useContactMessages";
import { ghlContactHref } from "@/lib/ghl-links";
import { buildActivity, firstName, formatDay, lastMessageLine } from "./drawerLogic";
import type { QuotePaymentRow, QuoteResponseRow } from "./useQuoteDrawerData";

interface ActivitySectionProps {
  quote: QuoteRow;
  lastMessage: ContactMessage | null;
  responses: QuoteResponseRow[];
  payments: QuotePaymentRow[];
}

const RESPONSE_KIND: Record<string, string> = {
  accepted: "Accepted", declined: "Declined", revision_requested: "Asked for a change",
};

const linkCls = "text-[13px] font-bold text-accent hover:underline disabled:opacity-45";

/** Messages & activity: the last message, client responses, the activity list and record metadata (#1559). */
export function ActivitySection({ quote, lastMessage, responses, payments }: ActivitySectionProps) {
  const [thread, setThread] = useState(false);
  const first = firstName(quote.client_name);
  const ghl = ghlContactHref(quote.contact?.ghl_location_id, quote.contact?.ghl_contact_id);
  const activity = buildActivity({
    quote, responses, payments,
    pendingAmount: quote.pending_etransfer_amount, pendingAt: quote.pending_etransfer_requested_at,
  });
  return (
    <section data-section="activity" className="rounded-[10px] border bg-card">
      <div className="border-b px-3.5 py-2.5">
        <h3 className="font-display text-[15px] font-bold">Messages &amp; activity</h3>
      </div>
      <div className="grid gap-2.5 px-3.5 py-3">
        <div className="flex flex-wrap items-center justify-between gap-2.5 text-[13.5px]">
          <span><b>Last message:</b> {lastMessageLine(lastMessage, first)}</span>
          <span className="flex flex-wrap gap-3">
            {ghl ? (
              <a href={ghl} target="_blank" rel="noopener noreferrer" className={linkCls}>Message in GHL</a>
            ) : (
              <button type="button" disabled title="Not synced to GHL yet" className={linkCls}>Message in GHL</button>
            )}
            {quote.contact_id && (
              <button type="button" aria-expanded={thread} onClick={() => setThread((t) => !t)} className={linkCls}>
                Open in Inbox →
              </button>
            )}
          </span>
        </div>
        {thread && quote.contact_id && (
          <ContactConversation contactId={quote.contact_id} ghlContactId={quote.contact?.ghl_contact_id} ghlLocationId={quote.contact?.ghl_location_id} />
        )}
        {responses.map((r) => (
          <div key={r.id} className="text-[12.5px] text-muted-foreground">
            <b className="text-foreground">{RESPONSE_KIND[r.action] ?? r.action}</b> · {formatDay(r.created_at)}
            {r.message?.trim() && (
              <div className="mt-1 whitespace-pre-line rounded-md border-l-[3px] border-accent bg-card px-2.5 py-1.5 text-[13.5px] italic text-foreground">
                "{r.message.trim()}"
              </div>
            )}
          </div>
        ))}
        {activity.length > 0 && (
          <ul className="grid gap-[7px] text-[13px]">
            {activity.map(([when, text], i) => (
              <li key={`${when}-${i}`} className="grid grid-cols-[94px_1fr] gap-2.5">
                <span className="text-muted-foreground">{when}</span>
                {text}
              </li>
            ))}
          </ul>
        )}
        <RecordMetadata
          createdAt={quote.created_at}
          updatedAt={quote.updated_at}
          createdByName={quote.created_by_profile?.display_name}
          updatedByName={quote.updated_by_profile?.display_name}
          createdVia={quote.created_via}
          updatedVia={quote.updated_via}
          entity="quote"
          recordId={quote.id}
          className="flex flex-wrap gap-x-3"
        />
      </div>
    </section>
  );
}
